import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import Section from '../components/ui/Section';
import SectionHeader from '../components/ui/SectionHeader';
import Button from '../components/ui/Button';

const Contact = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    budget: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const budgetOptions = [
    t('contact.budgetOptions.under50k'),
    t('contact.budgetOptions.50k100k'),
    t('contact.budgetOptions.100k250k'),
    t('contact.budgetOptions.250k500k'),
    t('contact.budgetOptions.notSure'),
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('contact.nameRequired');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('contact.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.emailInvalid');
    }
    if (!formData.message.trim()) {
      newErrors.message = t('contact.messageRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      // Placeholder for form submission
      console.log('Form submitted:', formData);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          company: '',
          email: '',
          budget: '',
          message: '',
        });
      }, 3000);
    }
  };

  return (
    <Section id="contact" withFlyingElements={true}>
      <SectionHeader
        title={t('contact.title')}
        subtitle={t('contact.subtitle')}
      />
      <div className="max-w-2xl mx-auto">
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
          whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="space-y-6"
        >
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className={`block mb-2 font-medium ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
                {t('contact.name')} *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-lg backdrop-blur-sm border ${
                  errors.name ? 'border-afsc-error' : isDark ? 'bg-white/10 border-white/20 text-white placeholder-white/50' : 'bg-white/80 border-afsc-charcoal/20 text-afsc-charcoal placeholder-afsc-charcoal/50'
                } focus:outline-none focus:ring-2 focus:ring-afsc-gold focus:border-transparent`}
                placeholder={t('contact.placeholders.name')}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-afsc-error">{errors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="company" className={`block mb-2 font-medium ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
                {t('contact.company')}
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-lg backdrop-blur-sm border ${
                  isDark ? 'bg-white/10 border-white/20 text-white placeholder-white/50' : 'bg-white/80 border-afsc-charcoal/20 text-afsc-charcoal placeholder-afsc-charcoal/50'
                } focus:outline-none focus:ring-2 focus:ring-afsc-gold focus:border-transparent`}
                placeholder={t('contact.placeholders.company')}
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className={`block mb-2 font-medium ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
              {t('contact.email')} *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg backdrop-blur-sm border ${
                errors.email ? 'border-afsc-error' : isDark ? 'bg-white/10 border-white/20 text-white placeholder-white/50' : 'bg-white/80 border-afsc-charcoal/20 text-afsc-charcoal placeholder-afsc-charcoal/50'
              } focus:outline-none focus:ring-2 focus:ring-afsc-gold focus:border-transparent`}
              placeholder={t('contact.placeholders.email')}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-afsc-error">{errors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="budget" className={`block mb-2 font-medium ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
              {t('contact.budget')}
            </label>
            <select
              id="budget"
              name="budget"
              value={formData.budget}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg backdrop-blur-sm border ${
                isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-white/80 border-afsc-charcoal/20 text-afsc-charcoal'
              } focus:outline-none focus:ring-2 focus:ring-afsc-gold focus:border-transparent`}
            >
              <option value="" className={isDark ? 'bg-afsc-slate' : 'bg-afsc-offwhite'}>{t('contact.budget')}...</option>
              {budgetOptions.map((option) => (
                <option key={option} value={option} className={isDark ? 'bg-afsc-slate' : 'bg-afsc-offwhite'}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="message" className={`block mb-2 font-medium ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
              {t('contact.message')} *
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={6}
              className={`w-full px-4 py-3 rounded-lg backdrop-blur-sm border ${
                errors.message ? 'border-afsc-error' : isDark ? 'bg-white/10 border-white/20 text-white placeholder-white/50' : 'bg-white/80 border-afsc-charcoal/20 text-afsc-charcoal placeholder-afsc-charcoal/50'
              } focus:outline-none focus:ring-2 focus:ring-afsc-gold focus:border-transparent resize-none`}
              placeholder={t('contact.placeholders.message')}
            />
            {errors.message && (
              <p className="mt-1 text-sm text-afsc-error">{errors.message}</p>
            )}
          </div>

          {submitted && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`p-4 bg-afsc-gold/20 border border-afsc-gold rounded-lg text-center ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}
            >
              {t('contact.successMessage')}
            </motion.div>
          )}

          <Button
            type="submit"
            variant="primary"
            className="w-full text-lg py-4"
          >
            {t('contact.send')}
          </Button>
        </motion.form>
      </div>
    </Section>
  );
};

export default Contact;

